import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Table, TableHead, TableBody, TableRow, TableCell, IconButton, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import SydButton from '../../../../../../../commonDesign/Button'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import Switch from '../../../../../../../molecules/Switch'
import { formatDisplayName } from '../../../../../../../../utils/formatters'

const useStyles = makeStyles(() => ({
  table: {
    '& th, & td': {
      padding: '8px',
      fontSize: '0.875rem'
    }
  },
  inputCell: {
    '& input': {
      fontSize: '0.875rem',
      padding: '4px 8px'
    }
  },
  switchCell: {
    textAlign: 'center',
    width: '70px'
  },
  actionsCell: {
    width: '50px',
    textAlign: 'center'
  },
  nameCell: {
    fontWeight: 500,
    minWidth: '120px'
  },
  headerRow: {
    backgroundColor: 'var(--color-neutral-700)'
  }
}))

function ColumnConfigEditor ({
  columns,
  availableColumns,
  onAddColumn,
  onRemoveColumn,
  onUpdateColumn
}) {
  const classes = useStyles()
  const [columnToAdd, setColumnToAdd] = useState('')

  // Filter out columns that are already added
  const selectableColumns = useMemo(() => {
    const existingNames = new Set(columns.map(c => c.name))
    return (availableColumns || [])
      .filter(pc => !existingNames.has(pc.name))
      .map(pc => ({
        label: `${pc.name} (${pc.dataType})`,
        value: pc.name
      }))
  }, [columns, availableColumns])

  const handleAddColumn = useCallback(() => {
    if (!columnToAdd) return

    const sourceColumn = availableColumns.find(c => c.name === columnToAdd)
    if (sourceColumn) {
      onAddColumn({
        name: sourceColumn.name,
        displayName: formatDisplayName(sourceColumn.name),
        description: sourceColumn.description || null,
        formatString: null,
        sortable: sourceColumn.sortable !== false,
        filterable: sourceColumn.filterable !== false,
        hidden: sourceColumn.hidden || false,
        exclude: false
      })
      setColumnToAdd('')
    }
  }, [columnToAdd, availableColumns, onAddColumn])

  const handleFieldChange = useCallback((index, field, value) => {
    const updated = { ...columns[index], [field]: value }
    onUpdateColumn(index, updated)
  }, [columns, onUpdateColumn])

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h4 style={{ margin: 0, flex: 1 }}>Column Configuration ({columns.length})</h4>
      </div>

      <Table className={`opal-presentation-table zebra-rows primary ${classes.table}`}>
        <TableHead className='opt-thead'>
          <TableRow className={`opt-tr ${classes.headerRow}`}>
            <TableCell className='opt-th'>Name</TableCell>
            <TableCell className='opt-th'>Display Name</TableCell>
            <TableCell className='opt-th'>Format</TableCell>
            <TableCell className='opt-th' style={{ textAlign: 'center' }}>Sort</TableCell>
            <TableCell className='opt-th' style={{ textAlign: 'center' }}>Filter</TableCell>
            <TableCell className='opt-th' style={{ textAlign: 'center' }}>Hidden</TableCell>
            <TableCell className='opt-th' style={{ textAlign: 'center' }}>Exclude</TableCell>
            <TableCell className='opt-th' style={{ width: '50px' }} />
          </TableRow>
        </TableHead>
        <TableBody className='opt-tbody'>
          {columns.map((column, index) => (
            <TableRow className='opt-tr' key={column.name}>
              <TableCell className={`opt-td ${classes.nameCell}`}>
                {column.name}
              </TableCell>
              <TableCell className={`opt-td ${classes.inputCell}`}>
                <TextField
                  value={column.displayName || ''}
                  onChange={(e) => handleFieldChange(index, 'displayName', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder={column.name}
                />
              </TableCell>
              <TableCell className={`opt-td ${classes.inputCell}`}>
                <TextField
                  value={column.formatString || ''}
                  onChange={(e) => handleFieldChange(index, 'formatString', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='e.g., currency, date'
                />
              </TableCell>
              <TableCell className={`opt-td ${classes.switchCell}`}>
                <Switch
                  checked={column.sortable}
                  onChange={(e) => handleFieldChange(index, 'sortable', e.target.checked)}
                />
              </TableCell>
              <TableCell className={`opt-td ${classes.switchCell}`}>
                <Switch
                  checked={column.filterable}
                  onChange={(e) => handleFieldChange(index, 'filterable', e.target.checked)}
                />
              </TableCell>
              <TableCell className={`opt-td ${classes.switchCell}`}>
                <Switch
                  checked={column.hidden}
                  onChange={(e) => handleFieldChange(index, 'hidden', e.target.checked)}
                />
              </TableCell>
              <TableCell className={`opt-td ${classes.switchCell}`}>
                <Switch
                  checked={column.exclude}
                  onChange={(e) => handleFieldChange(index, 'exclude', e.target.checked)}
                />
              </TableCell>
              <TableCell className={`opt-td ${classes.actionsCell}`}>
                <IconButton size='small' onClick={() => onRemoveColumn(index)}>
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {columns.length === 0 && (
            <TableRow className='opt-tr'>
              <TableCell className='opt-td' colSpan={8} style={{ textAlign: 'center', color: 'var(--text-color-subtle)' }}>
                No columns configured. Select a purpose to pre-fill columns, or add manually below.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {selectableColumns.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '16px' }}>
          <div style={{ flex: 1, maxWidth: '300px' }}>
            <SydSelect
              options={selectableColumns}
              value={columnToAdd}
              onChange={(e) => setColumnToAdd(e.target.value)}
              placeholder='Select column to add'
            />
          </div>
          <SydButton
            onClick={handleAddColumn}
            disabled={!columnToAdd}
            icon='add'
            size='sm'
          >
            Add Column
          </SydButton>
        </div>
      )}
    </div>
  )
}

ColumnConfigEditor.propTypes = {
  columns: PropTypes.array.isRequired,
  availableColumns: PropTypes.array,
  onAddColumn: PropTypes.func.isRequired,
  onRemoveColumn: PropTypes.func.isRequired,
  onUpdateColumn: PropTypes.func.isRequired
}

ColumnConfigEditor.defaultProps = {
  availableColumns: []
}

export default ColumnConfigEditor
